import { tokensToCssVars, type CssVarMap } from '../theme';

export type TokensGalleryProps = {
  /**
   * Override the token source. When omitted, reads from the linked
   * `@axi/tokens/ts` package via `tokensToCssVars()`.
   */
  tokens?: CssVarMap;
};

/**
 * Preview every CSS variable published by `@axi/tokens` plus the local
 * `--axi-sidebar-w` and `--axi-topbar-height-mobile` keys kept in
 * `dashboard.css :root`.
 *
 * The table is rendered from the in-memory flat map (`tokensToCssVars`)
 * so that changes to the provider automatically surface here on the
 * next dev-server reload — no hardcoded list to drift.
 *
 * Each row that resolves to a color (`#xxx`, `#xxxxxx`, `rgba(…)`)
 * gets a small swatch on the left for visual cross-reference.
 */
function TokensGallery({ tokens }: TokensGalleryProps = {}) {
  const vars = tokens ?? tokensToCssVars();
  const entries = Object.entries(vars).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="axi-gallery">
      <header className="axi-gallery__topbar">
        <div className="axi-gallery__brand">
          <span className="axi-gallery__brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12,3 21,12 12,21 3,12" />
            </svg>
          </span>
          <span>Axi Design · Tokens</span>
        </div>
      </header>
      <main className="axi-gallery__main">
        <h1 className="axi-gallery__title">Design Tokens</h1>
        <p className="axi-gallery__sub">
          来自 <code>@axi/tokens</code> 的 {entries.length} 个 CSS 变量。
          切换主题(<code>&lt;style data-axi-theme&gt;</code>)后,这里列出的可覆盖键会随之变化。
        </p>
        <table className="axi-token-table">
          <thead>
            <tr>
              <th aria-label="色样" />
              <th>键名</th>
              <th>值</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <td className="axi-token-table__swatch">
                  {isColorish(value) ? (
                    <span
                      className="axi-token-swatch"
                      style={{ ['--swatch-bg' as string]: value }}
                    />
                  ) : null}
                </td>
                <td><code>{key}</code></td>
                <td className="axi-token-table__value"><code>{value}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}

function isColorish(value: string): boolean {
  return (
    /^#[0-9a-fA-F]{3,8}$/.test(value) ||
    value.startsWith('rgba(') ||
    value.startsWith('rgb(')
  );
}

export default TokensGallery;